import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Upload, 
  Search, 
  RefreshCw,
  Trash2,
  Edit2,
  FileSpreadsheet,
  Building2,
  User
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useClient } from "@/context/ClientContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TDS_SECTIONS = [
  { value: "194C", label: "194C - Contractor (2%/1%)", rate: 2 },
  { value: "194J", label: "194J - Professional (10%)", rate: 10 },
  { value: "194I(a)", label: "194I(a) - Rent Machinery (2%)", rate: 2 },
  { value: "194I(b)", label: "194I(b) - Rent Building (10%)", rate: 10 },
  { value: "194H", label: "194H - Commission (5%)", rate: 5 },
  { value: "194A", label: "194A - Interest (10%)", rate: 10 },
  { value: "194Q", label: "194Q - Purchase (0.1%)", rate: 0.1 },
];

const PartyLedgers = () => {
  const { selectedClient, clientId } = useClient();
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [editSection, setEditSection] = useState("");
  const [editRate, setEditRate] = useState("");
  const fileInputRef = useRef(null);

  const fetchLedgers = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const params = { client_id: clientId };
      if (search) params.search = search;
      const response = await axios.get(`${API}/ledgers`, { params });
      setLedgers(response.data);
    } catch (err) {
      toast.error("Failed to load ledgers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) fetchLedgers();
    else setLedgers([]);
  }, [clientId]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLedgers();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !clientId) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        setUploading(true);
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const data = lines.slice(1).map(line => {
          const values = line.split(',');
          const obj = {};
          headers.forEach((header, idx) => {
            const val = values[idx]?.trim();
            if (header.includes('name') || header.includes('ledger')) obj.name = val;
            else if (header.includes('pan')) obj.pan = val;
            else if (header.includes('gstin') || header.includes('gst')) obj.gstin = val;
            else if (header.includes('group') || header.includes('nature') || header.includes('parent')) obj.group = val;
            else if (header.includes('address')) obj.address = val;
            else if (header.includes('tds') && (val === '1' || val?.toLowerCase() === 'yes' || val?.toLowerCase() === 'true')) {
              obj.is_tds_party = true;
            }
          });
          
          // Auto-extract PAN from GSTIN if PAN not provided
          // GSTIN format: 2 digit state + 10 digit PAN + entity code + Z + check digit
          if (obj.gstin && obj.gstin.length >= 15 && !obj.pan) {
            const extractedPan = obj.gstin.substring(2, 12);
            // Validate PAN format: 5 letters + 4 digits + 1 letter
            if (/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(extractedPan)) {
              obj.pan = extractedPan;
            }
          }
          
          return obj;
        }).filter(item => item.name);

        const response = await axios.post(`${API}/ledgers/bulk?client_id=${clientId}`, data);
        toast.success(`Uploaded ${response.data.total} ledgers (${response.data.with_pan || 0} with PAN)`);
        fetchLedgers();
      } catch (err) {
        toast.error("Failed to upload file");
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleClearAll = async () => {
    if (!window.confirm("Delete all ledgers for this client?")) return;
    try {
      await axios.delete(`${API}/ledgers?client_id=${clientId}`);
      toast.success("All ledgers deleted");
      setLedgers([]);
    } catch (err) {
      toast.error("Failed to delete ledgers");
    }
  };

  const handleEditSection = (ledger) => {
    setSelectedLedger(ledger);
    setEditSection(ledger.manual_section || ledger.suggested_section || "");
    setEditRate(ledger.manual_rate || ledger.suggested_rate || "");
    setEditDialog(true);
  };

  const handleSaveSection = async () => {
    if (!selectedLedger) return;
    try {
      await axios.put(`${API}/ledgers/${selectedLedger.id}/section`, {
        party_id: selectedLedger.id,
        section: editSection,
        rate: parseFloat(editRate) || 0
      });
      toast.success("Section updated");
      setEditDialog(false);
      fetchLedgers();
    } catch (err) {
      toast.error("Failed to update section");
    }
  };

  const handleSectionChange = (value) => {
    setEditSection(value);
    const section = TDS_SECTIONS.find(s => s.value === value);
    if (section) setEditRate(section.rate.toString());
  };

  const handleToggleTdsParty = async (ledgerId, isTdsParty) => {
    try {
      await axios.put(`${API}/ledgers/${ledgerId}/tds-party`, { is_tds_party: isTdsParty });
      setLedgers(prev => prev.map(l => l.id === ledgerId ? { ...l, is_tds_party: isTdsParty } : l));
      toast.success(isTdsParty ? "Marked as TDS Party" : "Unmarked as TDS Party");
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  if (!selectedClient) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
        <Building2 className="w-16 h-16 mb-4 text-slate-300" />
        <p className="text-lg font-medium">No Client Selected</p>
        <p className="text-sm mt-1">Please select a client from the sidebar</p>
      </div>
    );
  }

  return (
    <div data-testid="party-ledgers-page">
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title">Party Ledgers</h1>
          <p className="page-subtitle">{selectedClient.name} - Party master data</p>
        </div>
        <div className="flex gap-2">
          <input type="file" accept=".csv,.txt" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload CSV
          </Button>
          <Button variant="outline" onClick={handleClearAll}>
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        </div>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder="Search by name or PAN..." value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1" />
            <Button type="submit"><Search className="w-4 h-4 mr-2" />Search</Button>
            <Button type="button" variant="outline" onClick={() => { setSearch(''); fetchLedgers(); }}><RefreshCw className="w-4 h-4" /></Button>
          </form>
        </CardContent>
      </Card>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900">CSV Format</p>
            <p className="text-sm text-blue-700 mt-1">Columns: <code className="bg-blue-100 px-1 rounded">Name, GSTIN, Group, TDS_Ledger</code></p>
            <p className="text-xs text-blue-600 mt-1">PAN auto-extracted from GSTIN. Mark TDS Ledger checkbox for ledgers like TDS Payable 194C, TDS 194J etc.</p>
          </div>
        </div>
      </div>

      <Card className="data-card">
        <CardHeader className="data-card-header">
          <CardTitle className="data-card-title">Party Ledgers ({ledgers.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
          ) : ledgers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="tds-table">
                <thead>
                  <tr>
                    <th className="w-20">TDS Ledger</th>
                    <th>Ledger Name</th>
                    <th>GSTIN</th>
                    <th>PAN / Entity Type</th>
                    <th>Group</th>
                    <th>TDS Section</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgers.map((ledger, idx) => (
                    <tr key={ledger.id}>
                      <td className="text-center">
                        <input 
                          type="checkbox" 
                          checked={ledger.is_tds_party || false}
                          onChange={(e) => handleToggleTdsParty(ledger.id, e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          title="Mark as TDS Ledger to sync transactions"
                        />
                      </td>
                      <td className="font-medium">{ledger.name}</td>
                      <td className="font-mono text-xs">{ledger.gstin || '-'}</td>
                      <td>
                        <div className="space-y-1">
                          <span className="pan-cell block">{ledger.pan || '-'}</span>
                          {ledger.is_company !== null && ledger.is_company !== undefined && (
                            ledger.is_company ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-purple-50 text-purple-700 border border-purple-200">
                                <Building2 className="w-3 h-3" /> Company
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-cyan-50 text-cyan-700 border border-cyan-200">
                                <User className="w-3 h-3" /> {ledger.pan_type || 'Non-Company'}
                              </span>
                            )
                          )}
                        </div>
                      </td>
                      <td>{ledger.group || '-'}</td>
                      <td>
                        {ledger.manual_section ? (
                          <span className="section-badge bg-emerald-50 text-emerald-700 border-emerald-200">{ledger.manual_section} ({ledger.manual_rate}%)</span>
                        ) : ledger.suggested_section ? (
                          <span className="section-badge">{ledger.suggested_section} ({ledger.suggested_rate}%)</span>
                        ) : '-'}
                      </td>
                      <td>
                        <Button variant="ghost" size="sm" onClick={() => handleEditSection(ledger)}><Edit2 className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <Upload className="empty-state-icon" />
              <p className="empty-state-title">No ledgers found</p>
              <p className="empty-state-description">Upload a CSV file with party ledger data</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit TDS Section</DialogTitle>
            <DialogDescription>Override the auto-classified TDS section</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-600">Party Name</Label>
              <p className="font-medium">{selectedLedger?.name}</p>
            </div>
            <div className="space-y-2">
              <Label>TDS Section</Label>
              <Select value={editSection} onValueChange={handleSectionChange}>
                <SelectTrigger><SelectValue placeholder="Select section" /></SelectTrigger>
                <SelectContent>
                  {TDS_SECTIONS.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>TDS Rate (%)</Label>
              <Input type="number" step="0.1" value={editRate} onChange={(e) => setEditRate(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveSection}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartyLedgers;
